import { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Space, Spin, Empty, Typography, Tabs, Tag, Input, Select, Tooltip } from 'antd';
import { FileExcelOutlined, FilePdfOutlined, TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { displayNumbers, displayNum } from '../../../../variables/numbersFunctions';
import { loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY } from '../etatsSinistresFunctions';

const { Text } = Typography;
const PRIMARY = '#242166';

// Libellé long de la branche pour l'en-tête de la colonne SAP (repli sur le code).
const BRANCHE_FULL = { GUM: 'GUICHET UNIQUE DE MOTO', VT: 'VEHICULE EN TRANSIT', TPV: 'TPV', MOTO: 'MOTO' };

const fmtMontant = (v) => displayNumbers(Math.round(Number(v) || 0)) || '0';       // écran (espace fine OK en HTML)
const fmtPdf = (v) => { const n = Math.round(Number(v) || 0); return n === 0 ? '0' : displayNum(n); }; // PDF : espaces normales
const fmtPct = (v) => (v || v === 0 ? `${(Number(v) || 0).toFixed(2)} %` : '');
const frDate = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '');

/**
 * État « SAP réparti » d'UNE branche (TPV / MOTO / tout code) — MÊME FORMAT que l'état Pool GUM/VT
 * (PoolGumVtEtat) en vue mono-branche : tableau croisé exercices × compagnies avec, par compagnie,
 * une Quote part + un Montant, colonne SAP total de la branche, lignes TOTAUX et TOTAUX PAR COMPAGNIE,
 * onglet « Détails par dossier », exports PDF/Excel identiques. `highlightCompagnie` met une compagnie en avant.
 */
const SapRepartiEtat = ({ branche, arreteAu, highlightCompagnie, inclureOgar = false }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const brancheFull = BRANCHE_FULL[branche] || branche;
    const arreteLabel = arreteAu ? frDate(arreteAu) : 'ce jour';
    const arreteFileLabel = arreteAu ? frDate(arreteAu).replace(/\//g, '-') : 'courant';
    const titre = `SAP ${branche} ${arreteAu ? `vus au ${arreteLabel}` : '(courant)'}`;

    useEffect(() => {
        setLoading(true);
        const params = { branche, inclureOgar };
        if (arreteAu) params.arreteAu = dayjs(arreteAu).format('YYYY-MM-DD');
        axiosInstance.get('/api/stats/sap/reparti', { params })
            .then((res) => setData(res.data))
            .catch((err) => { console.error(err); setData(null); })
            .finally(() => setLoading(false));
    }, [branche, arreteAu, inclureOgar]);

    // ── Onglet « Détails par dossier » (chargé paresseusement) ──
    const [activeTab, setActiveTab] = useState('etat');
    const [detailRows, setDetailRows] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailSearch, setDetailSearch] = useState('');
    const [detailExercice, setDetailExercice] = useState(null);

    useEffect(() => { setDetailRows(null); }, [branche, arreteAu, inclureOgar]);
    useEffect(() => {
        if (activeTab !== 'details' || detailRows !== null) return;
        setDetailLoading(true);
        const params = { branche, inclureOgar };
        if (arreteAu) params.arreteAu = dayjs(arreteAu).format('YYYY-MM-DD');
        axiosInstance.get('/api/stats/sap/reparti/detail', { params })
            .then((res) => setDetailRows(res.data || []))
            .catch((err) => { console.error(err); setDetailRows([]); })
            .finally(() => setDetailLoading(false));
    }, [activeTab, detailRows, branche, arreteAu, inclureOgar]);

    const detailFiltres = useMemo(() => {
        let rows = detailRows || [];
        if (highlightCompagnie) rows = rows.filter((r) => r.compagnieCode === highlightCompagnie);
        if (detailExercice) rows = rows.filter((r) => r.annee === detailExercice);
        const q = detailSearch.trim().toLowerCase();
        if (q) rows = rows.filter((r) =>
            [r.numeroSinistre, r.numeroSinistrem, r.numeroPolice, r.souscripteur, r.compagnieNom]
                .some((v) => (v || '').toLowerCase().includes(q)));
        return rows;
    }, [detailRows, highlightCompagnie, detailExercice, detailSearch]);

    const detailExercices = useMemo(
        () => Array.from(new Set((detailRows || []).map((r) => r.annee))).sort(),
        [detailRows]
    );
    const detailTotaux = useMemo(() => detailFiltres.reduce((acc, r) => ({
        evaluation: acc.evaluation + (Number(r.evaluation) || 0),
        regle: acc.regle + (Number(r.montantRegle) || 0),
        regleAnt: acc.regleAnt + (Number(r.regleAnterieur) || 0),
        regleExo: acc.regleExo + (Number(r.regleExercice) || 0),
        sap: acc.sap + (Number(r.sap) || 0),
    }), { evaluation: 0, regle: 0, regleAnt: 0, regleExo: 0, sap: 0 }), [detailFiltres]);

    const compagnies = data?.compagnies || [];

    const dataSource = useMemo(() => {
        if (!data) return [];
        const rows = (data.lignes || []).map((l, i) => {
            const m = {};
            (l.montants || []).forEach((x) => { m[x.compagnieCode] = x; });
            return { key: `l${i}`, __type: 'data', label: l.label, sapBranche: l.sapTotal, m };
        });
        const tp = {};
        (data.totauxParCompagnie || []).forEach((x) => { tp[x.compagnieCode] = x; });
        rows.push({ key: 'totaux', __type: 'totaux', label: 'TOTAUX', sapBranche: data.totalGeneral, tp });
        rows.push({ key: 'totComp', __type: 'totComp', label: 'TOTAUX PAR COMPAGNIE', tp });
        return rows;
    }, [data]);

    // Exercices + colonne SAP total → colSpan de la ligne "TOTAUX PAR COMPAGNIE".
    const baseCount = 2;

    const columns = useMemo(() => {
        const bold = (row) => (row.__type !== 'data' ? { fontWeight: 700 } : {});
        const base = [
            {
                title: 'Exercices', dataIndex: 'label', key: 'label', fixed: 'left', width: 130,
                onCell: (row) => (row.__type === 'totComp' ? { colSpan: baseCount } : {}),
                render: (v, row) => <span style={{ ...bold(row), color: row.__type !== 'data' ? PRIMARY : undefined }}>{v}</span>,
            },
            {
                title: brancheFull, dataIndex: 'sapBranche', key: 'sapBranche', align: 'right', width: 140,
                onCell: (row) => (row.__type === 'totComp' ? { colSpan: 0 } : {}),
                render: (v, row) => <span style={{ ...bold(row), color: PRIMARY }}>{fmtMontant(v)}</span>,
            },
        ];

        const montantOf = (row, code) => {
            if (row.__type === 'totComp' || row.__type === 'totaux') return fmtMontant(row.tp?.[code]?.totalCompagnie);
            return fmtMontant(row.m?.[code]?.montant);
        };
        const quotePartOf = (row, code) => (row.__type === 'data' ? fmtPct(row.m?.[code]?.quotePart) : '');

        const compCols = compagnies.map((c) => {
            const hi = highlightCompagnie && c.code === highlightCompagnie;
            const cellBg = hi ? { style: { background: '#fff7e6' } } : {};
            return {
                title: (
                    <Tooltip title={`${c.code} — ${c.nom || ''}`.trim()}>
                        <span style={{ whiteSpace: 'normal', lineHeight: 1.15, display: 'inline-block', ...(hi ? { color: PRIMARY, fontWeight: 700 } : {}) }}>
                            {c.nom || c.code}
                        </span>
                    </Tooltip>
                ),
                key: `c_${c.code}`,
                children: [
                    {
                        title: 'Quote part', key: `${c.code}_qp`, align: 'center', width: 90,
                        onCell: () => cellBg,
                        render: (_, row) => quotePartOf(row, c.code),
                    },
                    {
                        title: 'Montant',
                        children: [
                            {
                                title: branche, key: `${c.code}_m`, align: 'right', width: 120,
                                onCell: () => cellBg,
                                render: (_, row) => <span style={bold(row)}>{montantOf(row, c.code)}</span>,
                            },
                        ],
                    },
                ],
            };
        });

        return [...base, ...compCols];
    }, [compagnies, highlightCompagnie, brancheFull, branche]);

    // ── Export Excel (état croisé) ──
    const handleExcel = () => {
        if (!data) return;
        const flatHeaders = ['Exercices', brancheFull];
        compagnies.forEach((c) => { flatHeaders.push(`${c.nom} - Quote part`); flatHeaders.push(`${c.nom} - Montant`); });
        const num = (v) => Math.round(Number(v) || 0);
        const aoa = [[titre], ["Méthode d'évaluation : Dossier par dossier"], [], flatHeaders];

        (data.lignes || []).forEach((l) => {
            const m = {}; (l.montants || []).forEach((x) => { m[x.compagnieCode] = x; });
            const row = [l.label, num(l.sapTotal)];
            compagnies.forEach((c) => {
                row.push(fmtPct(m[c.code]?.quotePart));
                row.push(num(m[c.code]?.montant));
            });
            aoa.push(row);
        });

        const tp = {}; (data.totauxParCompagnie || []).forEach((x) => { tp[x.compagnieCode] = x; });
        const totRow = ['TOTAUX', num(data.totalGeneral)];
        compagnies.forEach((c) => { totRow.push(''); totRow.push(num(tp[c.code]?.totalCompagnie)); });
        aoa.push(totRow);

        const totCompRow = ['TOTAUX PAR COMPAGNIE', ''];
        compagnies.forEach((c) => { totCompRow.push(''); totCompRow.push(num(tp[c.code]?.totalCompagnie)); });
        aoa.push(totCompRow);

        aoa.push([]);
        aoa.push(["NB: Il revient à chaque société d'appliquer le taux minimal de chargement de 05% pour déterminer la Provision des SAP"]);
        aoa.push([`Fait le ${dayjs().format('DD/MM/YYYY')}`]);
        aoa.push(['Le Directeur du POOL TPVM-VT GIE']);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, `SAP ${branche}`);
        const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([blob], { type: 'application/octet-stream' }), `SAP_${branche}_au_${arreteFileLabel}.xlsx`);
    };

    // ── Export PDF (état croisé) ──
    const handlePdf = async () => {
        if (!data) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
        const pw = doc.internal.pageSize.getWidth();
        const formattedNow = dayjs().format('DD/MM/YYYY HH:mm');
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const head1 = [{ content: 'Exercices', rowSpan: 2 }, { content: brancheFull, rowSpan: 2 }];
        const head2 = [];
        compagnies.forEach((c) => {
            head1.push({ content: c.nom, colSpan: 2, styles: { halign: 'center' } });
            head2.push('Quote part'); head2.push(branche);
        });

        const body = (data.lignes || []).map((l) => {
            const m = {}; (l.montants || []).forEach((x) => { m[x.compagnieCode] = x; });
            const row = [l.label, fmtPdf(l.sapTotal)];
            compagnies.forEach((c) => { row.push(fmtPct(m[c.code]?.quotePart)); row.push(fmtPdf(m[c.code]?.montant)); });
            return row;
        });

        const tp = {}; (data.totauxParCompagnie || []).forEach((x) => { tp[x.compagnieCode] = x; });
        const totRow = [{ content: 'TOTAUX', styles: { fontStyle: 'bold' } }, fmtPdf(data.totalGeneral)];
        compagnies.forEach((c) => { totRow.push(''); totRow.push(fmtPdf(tp[c.code]?.totalCompagnie)); });
        body.push(totRow);

        const totCompRow = [{ content: 'TOTAUX PAR COMPAGNIE', colSpan: baseCount, styles: { halign: 'right', fontStyle: 'bold' } }];
        compagnies.forEach((c) => {
            totCompRow.push({ content: fmtPdf(tp[c.code]?.totalCompagnie), colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } });
        });
        body.push(totCompRow);

        autoTable(doc, {
            head: [head1, head2], body, startY: 34, theme: 'grid',
            styles: { fontSize: 6.5, cellPadding: 1, lineWidth: 0.1, lineColor: [210, 210, 220], overflow: 'linebreak' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, halign: 'center' },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: { 0: { halign: 'left', cellWidth: 22 } },
            margin: { top: 34, bottom: 22, left: 10, right: 10 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: titre, subtitle: "Méthode d'évaluation : Dossier par dossier", enteteData, logos, formattedNow,
            }),
        });

        let y = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(8); doc.setFont(undefined, 'italic'); doc.setTextColor(60, 60, 60);
        doc.text("NB: Il revient à chaque société d'appliquer le taux minimal de chargement de 05% pour déterminer la Provision des SAP", 12, y);
        y += 12; doc.setFont(undefined, 'normal');
        doc.text(`Fait le ${dayjs().format('DD/MM/YYYY')}`, pw - 90, y);
        y += 7; doc.setFont(undefined, 'bold');
        doc.text('Le Directeur du POOL TPVM-VT GIE', pw - 90, y);

        finalizeEtatPdf(doc);
        doc.save(`SAP_${branche}_au_${arreteFileLabel}.pdf`);
    };

    // ── Détails par dossier (mêmes colonnes / exports que l'état GUM/VT) ──
    const titreDetail = `Détail des SAP ${branche} par dossier ${arreteAu ? `au ${arreteLabel}` : '(courant)'}`;
    const DETAIL_HEADERS = ['Exercice', 'Branche', 'N° Sinistre', 'N° Sinistre Cie', 'Survenance',
        'N° Police', 'Assuré', 'Compagnie', 'Évaluation', 'Réglé exos ant.', 'Réglé exercice', 'SAP'];
    const detailRowCells = (r, fm) => [
        String(r.annee ?? ''), r.branche, r.numeroSinistre || '', r.numeroSinistrem || '',
        frDate(r.dateSinistre), r.numeroPolice || '', r.souscripteur || '', r.compagnieNom || '',
        fm(r.evaluation), fm(r.regleAnterieur), fm(r.regleExercice), fm(r.sap),
    ];

    const handleDetailExcel = () => {
        if (!detailFiltres.length) return;
        const num = (v) => Math.round(Number(v) || 0);
        const aoa = [[titreDetail], ["Méthode d'évaluation : Dossier par dossier"], [], DETAIL_HEADERS];
        detailFiltres.forEach((r) => aoa.push(detailRowCells(r, num)));
        aoa.push(['TOTAUX', '', '', '', '', '', '', `${detailFiltres.length} dossier(s)`,
            num(detailTotaux.evaluation), num(detailTotaux.regleAnt), num(detailTotaux.regleExo), num(detailTotaux.sap)]);
        aoa.push([]); aoa.push([`Fait le ${dayjs().format('DD/MM/YYYY')}`]); aoa.push(['Le Directeur du POOL TPVM-VT GIE']);

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Détail SAP');
        const blob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([blob], { type: 'application/octet-stream' }), `Detail_SAP_${branche}_au_${arreteFileLabel}.xlsx`);
    };

    const handleDetailPdf = async () => {
        if (!detailFiltres.length) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const formattedNow = dayjs().format('DD/MM/YYYY HH:mm');
        const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

        const body = detailFiltres.map((r) => detailRowCells(r, fmtPdf));
        body.push([
            { content: 'TOTAUX', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `${detailFiltres.length} dossier(s)`, styles: { fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.evaluation), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.regleAnt), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.regleExo), styles: { halign: 'right', fontStyle: 'bold' } },
            { content: fmtPdf(detailTotaux.sap), styles: { halign: 'right', fontStyle: 'bold' } },
        ]);

        autoTable(doc, {
            head: [DETAIL_HEADERS], body, startY: 34, theme: 'grid',
            styles: { fontSize: 6.5, cellPadding: 1, lineWidth: 0.1, lineColor: [210, 210, 220], overflow: 'linebreak' },
            headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, halign: 'center' },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            columnStyles: { 8: { halign: 'right' }, 9: { halign: 'right' }, 10: { halign: 'right' } },
            margin: { top: 34, bottom: 22, left: 8, right: 8 },
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: titreDetail, subtitle: "Méthode d'évaluation : Dossier par dossier", enteteData, logos, formattedNow,
            }),
        });

        finalizeEtatPdf(doc);
        doc.save(`Detail_SAP_${branche}_au_${arreteFileLabel}.pdf`);
    };

    const detailColumns = [
        { title: 'Exercice', dataIndex: 'annee', key: 'annee', width: 90, render: (v) => String(v ?? '') },
        {
            title: 'Branche', dataIndex: 'branche', key: 'branche', width: 70, align: 'center',
            render: (v) => <Tag color="blue" style={{ margin: 0 }}>{v}</Tag>,
        },
        {
            title: 'N° Sinistre', key: 'numeroSinistre',
            render: (_, r) => (
                <div>
                    <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.numeroSinistre || '—'}</div>
                    {r.numeroSinistrem && <div style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace' }}>{r.numeroSinistrem}</div>}
                </div>
            ),
        },
        { title: 'Survenance', dataIndex: 'dateSinistre', key: 'dateSinistre', width: 95, render: (v) => frDate(v) },
        { title: 'N° Police', dataIndex: 'numeroPolice', key: 'numeroPolice', render: (v) => <span style={{ fontFamily: 'monospace', color: '#666' }}>{v || '—'}</span> },
        { title: 'Assuré', dataIndex: 'souscripteur', key: 'souscripteur' },
        { title: 'Compagnie', dataIndex: 'compagnieNom', key: 'compagnieNom' },
        { title: 'Évaluation', dataIndex: 'evaluation', key: 'evaluation', align: 'right', render: (v) => fmtMontant(v) },
        { title: 'Réglé exos ant.', dataIndex: 'regleAnterieur', key: 'regleAnterieur', align: 'right', render: (v) => fmtMontant(v) },
        { title: 'Réglé exercice', dataIndex: 'regleExercice', key: 'regleExercice', align: 'right', render: (v) => fmtMontant(v) },
        { title: 'SAP', dataIndex: 'sap', key: 'sap', align: 'right', render: (v) => <b style={{ color: PRIMARY }}>{fmtMontant(v)}</b> },
    ];

    const etatContent = (
        <Card
            title={
                <div>
                    <div style={{ fontWeight: 700, color: PRIMARY, fontSize: 16 }}>{titre}</div>
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>Méthode d'évaluation : Dossier par dossier</Text>
                </div>
            }
            extra={
                <Space>
                    <Button icon={<FileExcelOutlined />} onClick={handleExcel} disabled={!data?.lignes?.length} style={{ borderColor: '#059669', color: '#059669' }}>Excel</Button>
                    <Button icon={<FilePdfOutlined />} onClick={handlePdf} disabled={!data?.lignes?.length} style={{ borderColor: '#dc2626', color: '#dc2626' }}>PDF</Button>
                </Space>
            }
        >
            <Spin spinning={loading}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: <Empty description={`Aucune donnée SAP ${branche}`} /> }}
                    onRow={(row) => (row.__type !== 'data' ? { style: { background: '#f2f2f8' } } : {})}
                />
            </Spin>
            <div style={{ marginTop: 16, fontStyle: 'italic', color: '#595959', fontSize: 12 }}>
                NB : Il revient à chaque société d'appliquer le taux minimal de chargement de 05 % pour déterminer la Provision des SAP.
            </div>
            <div style={{ marginTop: 24, textAlign: 'right' }}>
                <div>Fait le {dayjs().format('DD/MM/YYYY')}</div>
                <div style={{ fontWeight: 700, marginTop: 8 }}>Le Directeur du POOL TPVM-VT GIE</div>
            </div>
        </Card>
    );

    const detailContent = (
        <Card
            title={
                <div>
                    <div style={{ fontWeight: 700, color: PRIMARY, fontSize: 16 }}>{titreDetail}</div>
                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                        Un dossier par ligne — les totaux SAP recoupent les cellules de l'état croisé.
                    </Text>
                </div>
            }
            extra={
                <Space>
                    <Button icon={<FileExcelOutlined />} onClick={handleDetailExcel} disabled={!detailFiltres.length} style={{ borderColor: '#059669', color: '#059669' }}>Excel</Button>
                    <Button icon={<FilePdfOutlined />} onClick={handleDetailPdf} disabled={!detailFiltres.length} style={{ borderColor: '#dc2626', color: '#dc2626' }}>PDF</Button>
                </Space>
            }
        >
            <Space wrap style={{ marginBottom: 12 }}>
                <Select
                    allowClear placeholder="Exercice…" style={{ width: 140 }}
                    value={detailExercice || undefined} onChange={(v) => setDetailExercice(v || null)}
                    options={detailExercices.map((a) => ({ value: a, label: String(a) }))}
                />
                <Input.Search
                    allowClear placeholder="N° sinistre, police, assuré, compagnie…" style={{ width: 320 }}
                    onSearch={setDetailSearch} onChange={(e) => { if (!e.target.value) setDetailSearch(''); }}
                />
                <Text type="secondary">
                    {detailFiltres.length} dossier(s) — SAP total : <b style={{ color: PRIMARY }}>{fmtMontant(detailTotaux.sap)}</b>
                </Text>
            </Space>
            <Spin spinning={detailLoading}>
                <Table
                    rowKey={(r) => r.sinistreCodeId || `${r.numeroSinistre}-${r.branche}`}
                    columns={detailColumns}
                    dataSource={detailFiltres}
                    bordered
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} dossier(s)` }}
                    locale={{ emptyText: <Empty description={`Aucun dossier SAP ${branche}`} /> }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ background: '#f2f2f8', fontWeight: 700 }}>
                                <Table.Summary.Cell index={0} colSpan={7}>TOTAUX ({detailFiltres.length} dossier(s))</Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align="right">{fmtMontant(detailTotaux.evaluation)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align="right">{fmtMontant(detailTotaux.regleAnt)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={9} align="right">{fmtMontant(detailTotaux.regleExo)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={10} align="right"><span style={{ color: PRIMARY }}>{fmtMontant(detailTotaux.sap)}</span></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </Spin>
        </Card>
    );

    return (
        <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
                { key: 'etat', label: <span><TableOutlined /> État</span>, children: etatContent },
                { key: 'details', label: <span><UnorderedListOutlined /> Détails par dossier</span>, children: detailContent },
            ]}
        />
    );
};

export default SapRepartiEtat;
